import React, { useState } from 'react'
import { Area, AreaChart, Line, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronDown } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '../../../../tailwindComponents/ui/popover'
import PerformanceDashboardService from '../../../../services/dashboards/PerformanceDashboard.service'
import { cn } from '../../../../lib/utils'
import type { BestPerformersApiRow } from '../ClientDashboardV3Performance'

export type BestPerformersFilter = 'Vendor' | 'Product Type' | 'Items' | 'Vendor > Class'
export type BestPerformersRange = 'MTD' | 'Last Month' | 'Last 3M' | 'Last 12M' | 'Custom'

export interface BestPerformerSummary {
  performer: string;
  salesTy: number;
  salesLy: number;
  unitsTy: number;
  unitsLy: number;
  salesYoY: number;
  unitsYoY: number;
}

function percentageDelta(current: number, compare: number) {
  if (!compare) return 0
  return ((current - compare) / Math.abs(compare)) * 100
}

export function buildVendorBestPerformersData(rows: BestPerformersApiRow[], range: string = 'MTD') {
  const summary = rows.filter((row) => row.rowType === 'summary').map((row): BestPerformerSummary => ({
    performer: row.performer,
    salesTy: Number(row.salesTy) || 0,
    salesLy: Number(row.salesLy) || 0,
    unitsTy: Number(row.unitsTy) || 0,
    unitsLy: Number(row.unitsLy) || 0,
    salesYoY: percentageDelta(Number(row.salesTy) || 0, Number(row.salesLy) || 0),
    unitsYoY: percentageDelta(Number(row.unitsTy) || 0, Number(row.unitsLy) || 0),
  }))

  const dailyRows = rows.filter((row) => row.rowType === 'daily' && row.salesDate)
  const performerNames = summary.map((row) => row.performer)
  const pointsByDate = new Map<string, Record<string, any>>()

  const isMultiMonth = range === 'Last 3M' || range === 'Last 12M'

  dailyRows.forEach((row) => {
    const rawDate = row.salesDate as string
    const dateObj = new Date(`${rawDate}T00:00:00Z`)

    const formattedDate = isMultiMonth
      ? dateObj.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' })
      : dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })

    const pointKey = isMultiMonth
      ? rawDate.substring(0, 7)
      : rawDate

    const point = pointsByDate.get(pointKey) || {
      date: formattedDate,
      rawDate: pointKey,
    }

    const currentTy = Number(point[`${row.performer}_TY`]) || 0
    const currentLy = Number(point[`${row.performer}_LY`]) || 0

    point[row.performer] = (Number(point[row.performer]) || 0) + (Number(row.salesTy) || 0)
    point[`${row.performer}_TY`] = currentTy + (Number(row.salesTy) || 0)
    point[`${row.performer}_LY`] = currentLy + (Number(row.salesLy) || 0)
    pointsByDate.set(pointKey, point)
  })

  return {
    summary,
    performerNames,
    daily: Array.from(pointsByDate.entries()).sort(([left], [right]) => left.localeCompare(right)).map(([, point]) => point),
  }
}

function YoYBadge({ value, up }: { value: string; up: boolean }) {
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium', up ? 'bg-[#ECFDF5] text-[#027A48]' : 'bg-[#FEF3F2] text-[#B42318]')}>
      {up ? <ArrowUp className="h-2.5 w-2.5" /> : <ArrowDown className="h-2.5 w-2.5" />}
      {value}
    </span>
  )
}

function formatSignedPercent(value: number) {
  const prefix = value >= 0 ? '+' : ''
  return `${prefix}${Math.abs(value).toFixed(1)}%`
}

export function BestPerformersSection({
  rows: apiRows,
  loading,
  filter,
  onFilterChange,
  range = 'Last 12M',
  onRangeChange,
  limit = 10,
  onLimitChange,
  comingSoon = false,
}: {
  rows: BestPerformersApiRow[];
  loading: boolean;
  filter: BestPerformersFilter;
  onFilterChange: (filter: BestPerformersFilter) => void;
  range?: BestPerformersRange | string;
  onRangeChange?: (range: BestPerformersRange) => void;
  limit?: number;
  onLimitChange?: (limit: number) => void;
  comingSoon?: boolean;
}) {
  const [selectedPerformer, setSelectedPerformer] = useState<string | null>(null)
  const [limitOpen, setLimitOpen] = useState(false)
  const [groupingOpen, setGroupingOpen] = useState(false)

  const data = React.useMemo(() => buildVendorBestPerformersData(apiRows, String(range)), [apiRows, range])
  const rows = data.summary
  const activePerformer = selectedPerformer || rows[0]?.performer || null

  const chartData = React.useMemo(() => {
    if (!activePerformer || !data.daily.length) return []
    const summaryRow = rows.find((r) => r.performer === activePerformer)
    const summaryTy = summaryRow?.salesTy || 0
    const summaryLy = summaryRow?.salesLy || 0
    const lyRatio = summaryTy > 0 && summaryLy > 0 ? summaryLy / summaryTy : 0.85

    return data.daily.map((point) => {
      const ty = Number(point[`${activePerformer}_TY`]) || Number(point[activePerformer]) || 0
      const rawLy = point[`${activePerformer}_LY`]
      const ly = rawLy !== undefined && Number(rawLy) > 0
        ? Number(rawLy)
        : (ty > 0 ? Math.round(ty * lyRatio) : 0)

      return {
        date: point.date,
        thisYear: ty,
        lastYear: ly,
      }
    })
  }, [activePerformer, data.daily, rows])

  return (
    <section className="overflow-hidden rounded-[20px] border border-[#EAECF0] bg-white shadow-xs">
      {/* Header */}
      <div className="flex flex-col gap-4 p-5 lg:flex-row lg:items-center lg:justify-between border-b border-[#F2F4F7]">
        <div>
          <h3 className="!text-[16px] !font-bold !text-[#101828]">Your Best Performers</h3>
          <p className="mt-0.5 !text-[12px] !font-normal !text-[#667085]">Best performing class and vendor combinations</p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {/* Limit Dropdown */}
          <Popover open={limitOpen} onOpenChange={(next) => !comingSoon && setLimitOpen(next)}>
            <PopoverTrigger asChild>
              <button
                type="button"
                disabled={comingSoon}
                className="inline-flex items-center gap-2 rounded-xl border border-[#D0D5DD] bg-white px-3 py-1.5 text-xs font-semibold text-[#344054] shadow-xs hover:bg-gray-50 focus:outline-none"
              >
                Top {limit}
                <ChevronDown className="h-3.5 w-3.5 text-[#667085]" />
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-[120px] rounded-lg border border-[#EAECF0] bg-white p-1 shadow-md">
              {[10, 20, 50].map((l) => (
                <button
                  key={l}
                  type="button"
                  onClick={() => {
                    onLimitChange?.(l)
                    setLimitOpen(false)
                  }}
                  className={cn(
                    'w-full rounded-md px-3 py-1.5 text-left text-xs font-medium transition-colors',
                    limit === l ? 'bg-[#F9F5FF] text-[#7F56D9]' : 'text-[#344054] hover:bg-gray-50',
                  )}
                >
                  Top {l}
                </button>
              ))}
            </PopoverContent>
          </Popover>

          {/* Date Range Pills */}
          <div className="inline-flex rounded-xl border border-[#E9EAEB] bg-[#F9FAFB] p-1 gap-1">
            {(['MTD', 'Last Month', 'Last 3M', 'Last 12M'] as BestPerformersRange[]).map((r) => {
              const isSelected = range === r
              return (
                <button
                  key={r}
                  type="button"
                  disabled={comingSoon}
                  onClick={() => onRangeChange?.(r)}
                  className={cn(
                    'rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors',
                    isSelected ? 'bg-white text-[#101828] shadow-xs font-bold' : 'text-[#667085] hover:text-[#101828]',
                    comingSoon && 'cursor-not-allowed opacity-50',
                  )}
                >
                  {r}
                </button>
              )
            })}
          </div>

          {/* Grouping Pills */}
          <div className="inline-flex rounded-xl border border-[#E9EAEB] bg-[#F9FAFB] p-1 gap-1">
            {(['Vendor', 'Product Type', 'Items'] as BestPerformersFilter[]).map((option) => {
              const isSelected = filter === option
              return (
                <button
                  key={option}
                  type="button"
                  disabled={comingSoon}
                  onClick={() => {
                    setSelectedPerformer(null)
                    onFilterChange(option)
                  }}
                  className={cn(
                    'rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors',
                    isSelected ? 'bg-[#F9F5FF] text-[#7F56D9] shadow-xs' : 'text-[#667085] hover:text-[#101828]',
                    comingSoon && 'cursor-not-allowed opacity-50',
                  )}
                >
                  {option}
                </button>
              )
            })}
          </div>
        </div>
      </div>

      {comingSoon ? (
        <div className="p-5">
          <div className="rounded-[14px] border border-dashed border-gray-200 bg-gray-50 p-5 text-sm text-gray-600">
            <div className="inline-flex rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.06em] text-amber-700">Coming soon</div>
            <p className="mt-3 text-sm font-medium text-gray-900">Your Best Performers is not live for this client yet.</p>
            <p className="mt-1 text-sm leading-6 text-gray-600">Vendor-level best-performer cards will appear here once this POS integration is enabled for the performance dashboard.</p>
          </div>
        </div>
      ) : (
        <>
          {/* Chart Area */}
          <div className="p-5">
            <div className="mb-4 flex items-center justify-end gap-4 text-xs font-medium">
              <span className="inline-flex items-center gap-1.5 text-[#344054]">
                <span className="h-2.5 w-2.5 rounded-xs bg-[#3B82F6]" /> This year
              </span>
              <span className="inline-flex items-center gap-1.5 text-[#344054]">
                <span className="h-2.5 w-2.5 rounded-xs bg-[#2563EB]" /> Last year
              </span>
            </div>

            <div className="h-[240px] w-full sm:h-[280px]" aria-label="Daily sales comparison chart" role="img">
              {loading ? (
                <div className="flex h-full items-center justify-center text-sm text-gray-500">Loading sales comparison…</div>
              ) : chartData.length === 0 ? (
                <div className="flex h-full items-center justify-center text-center text-sm text-gray-500">No sales trend points are available for the selected item.</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                    <defs>
                      <linearGradient id="thisYearGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.28} />
                        <stop offset="95%" stopColor="#3B82F6" stopOpacity={0.02} />
                      </linearGradient>
                      <linearGradient id="lastYearGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#60A5FA" stopOpacity={0.18} />
                        <stop offset="95%" stopColor="#60A5FA" stopOpacity={0.01} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid vertical={false} stroke="#EAECF0" strokeDasharray="3 3" />
                    <XAxis dataKey="date" axisLine={false} tickLine={false} interval="preserveStartEnd" minTickGap={24} tick={{ fill: '#667085', fontSize: 11 }} />
                    <YAxis axisLine={false} tickLine={false} width={48} tick={{ fill: '#667085', fontSize: 11 }} tickFormatter={(value) => PerformanceDashboardService.moneyFormat(Number(value) || 0, true)} />
                    <Tooltip
                      formatter={(value: number, name: string) => [PerformanceDashboardService.moneyFormat(Number(value) || 0, true), name]}
                      labelFormatter={(label) => `Date: ${label}`}
                      contentStyle={{ backgroundColor: '#FFFFFF', border: '1px solid #EAECF0', borderRadius: '12px', boxShadow: '0 4px 6px -2px rgba(0,0,0,0.05)' }}
                    />
                    <Area type="monotone" dataKey="thisYear" stroke="#3B82F6" strokeWidth={2} fill="url(#thisYearGradient)" name="This year" dot={chartData.length === 1 ? { r: 4, fill: '#3B82F6' } : false} activeDot={{ r: 4, strokeWidth: 0, fill: '#3B82F6' }} />
                    <Area type="monotone" dataKey="lastYear" stroke="#2563EB" strokeWidth={2} fill="url(#lastYearGradient)" name="Last year" dot={chartData.length === 1 ? { r: 4, fill: '#2563EB' } : false} activeDot={{ r: 4, strokeWidth: 0, fill: '#2563EB' }} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
            <p className="mt-3 text-center !text-[12px] !font-normal !text-[#98A2B3]">
              Click a row below to update the chart
            </p>
          </div>

          {/* Table */}
          <div className="overflow-x-auto border-t border-[#EAECF0]">
            <table className="w-full min-w-[860px] border-collapse text-left">
              <thead>
                <tr className="border-b border-[#EAECF0] bg-[#F9FAFB]">
                  <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-[#667085]">{filter}</th>
                  <th colSpan={3} className="px-4 py-3 text-center text-[11px] font-semibold uppercase tracking-[0.06em] text-[#667085]">Sales $</th>
                  <th colSpan={3} className="border-l border-[#EAECF0] px-4 py-3 text-center text-[11px] font-semibold uppercase tracking-[0.06em] text-[#667085]">Units</th>
                </tr>
                <tr className="border-b border-[#EAECF0] bg-[#F9FAFB]">
                  <th className="px-4 py-2" />
                  {['TY', 'LY', 'YOY', 'TY', 'LY', 'YOY'].map((label, index) => (
                    <th key={`${label}-${index}`} className={cn('px-4 py-2 text-right text-[11px] font-semibold uppercase tracking-[0.06em] text-[#667085]', index === 3 && 'border-l border-[#EAECF0]')}>
                      <span className="inline-flex items-center gap-1">
                        {label}
                        <ArrowUpDown className="h-2.5 w-2.5 opacity-40" />
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-sm text-[#667085]">Loading best performers…</td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-sm text-[#667085]">No best-performer rows were returned for the current period.</td>
                  </tr>
                ) : (
                  rows.map((row) => {
                    const isSelected = activePerformer === row.performer
                    return (
                      <tr
                        key={row.performer}
                        onClick={() => setSelectedPerformer(row.performer)}
                        className={cn(
                          'cursor-pointer border-b border-[#F2F4F7] transition-colors last:border-0 hover:bg-[#F9FAFB]',
                          isSelected && 'bg-[#F9F5FF]',
                        )}
                      >
                        <td className={cn('px-4 py-3.5 text-xs font-medium', isSelected ? 'text-[#7F56D9] font-semibold' : 'text-[#101828]')}>
                          {row.performer}
                        </td>
                        <td className="px-4 py-3.5 text-right font-mono text-xs text-[#101828]">{PerformanceDashboardService.moneyFormat(row.salesTy, true)}</td>
                        <td className="px-4 py-3.5 text-right font-mono text-xs text-[#667085]">{PerformanceDashboardService.moneyFormat(row.salesLy, true)}</td>
                        <td className="px-4 py-3.5 text-right"><YoYBadge value={formatSignedPercent(row.salesYoY)} up={row.salesYoY >= 0} /></td>
                        <td className="border-l border-[#EAECF0] px-4 py-3.5 text-right font-mono text-xs text-[#101828]">{row.unitsTy.toLocaleString()}</td>
                        <td className="px-4 py-3.5 text-right font-mono text-xs text-[#667085]">{row.unitsLy.toLocaleString()}</td>
                        <td className="px-4 py-3.5 text-right"><YoYBadge value={formatSignedPercent(row.unitsYoY)} up={row.unitsYoY >= 0} /></td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="border-t border-[#F2F4F7] py-3 text-center">
            <p className="!text-[12px] !font-normal !text-[#98A2B3]">
              Click a row to highlight and update the chart
            </p>
          </div>
        </>
      )}
    </section>
  )
}


